import {verifiedFetch} from '@github-ui/verified-fetch'
import {LinkExternalIcon, PlusIcon, ToolsIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Dialog, FormControl, TextInput} from '@primer/react'
import type React from 'react'
import {type RefObject, useCallback, useRef, useState} from 'react'

import styles from './ConfigureMcpServersMenu.module.css'

const MCP_SPEC_LINK = (
  <a
    href="https://modelcontextprotocol.io/specification/2025-06-18/basic/authorization"
    target="_blank"
    rel="noopener noreferrer"
    style={{textDecoration: 'underline'}}
  >
    MCP authorization specification
  </a>
)

export interface ConfigureMcpServersMenuProps {
  anchorRef: RefObject<HTMLButtonElement>
  panel: 'add-mcp' | null
  onPanelChange: (panel: 'add-mcp' | null) => void
}

export const ConfigureMcpServersMenu: React.FC<ConfigureMcpServersMenuProps> = ({panel, onPanelChange}) => {
  const [showDialog, setShowDialog] = useState(false)
  const open = panel === 'add-mcp' || showDialog
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [urlError, setUrlError] = useState<string | JSX.Element | null>(null)

  // When dialog closes, just hide dialog, keep overlay open
  const onDialogClose = useCallback(() => {
    setShowDialog(false)
    setName('')
    setUrl('')
    setUrlError(null)
    onPanelChange(null)
  }, [onPanelChange])

  // When overlay closes, also hide dialog and reset form
  const handleOverlayClose = useCallback(() => {
    if (!showDialog) {
      onPanelChange(null)
      setShowDialog(false)
      setName('')
      setUrl('')
      setUrlError(null)
    }
  }, [onPanelChange, showDialog])

  function isValidHttpsUrl(inputUrl: string): boolean {
    try {
      const parsedUrl = new URL(inputUrl.trim(), window.location.origin)
      return (
        parsedUrl.protocol === 'https:' &&
        !!parsedUrl.hostname &&
        /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(parsedUrl.hostname)
      )
    } catch {
      return false
    }
  }

  const isFormValid = () => {
    return url.trim() !== ''
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    if (!isFormValid()) {
      // Show validation errors
      if (!url.trim()) {
        setUrlError('MCP server URL is required')
      }
      return
    }

    try {
      setUrlError(null)

      // Validate HTTPS URL format before sending to backend
      if (!isValidHttpsUrl(url)) {
        setUrlError('Invalid HTTPS URL format')
        return
      }

      // Send MCP server details to backend for OAuth setup
      const formData = new FormData()
      formData.append('mcp_server_url', url.trim())
      formData.append('name', name.trim())
      const response = await verifiedFetch('/copilot/mcp/servers', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        let errorMessage: string | JSX.Element | null = 'Failed to add MCP server'

        try {
          // Read response body once as text, then try to parse as JSON
          const responseText = await response.text()

          try {
            const errorData = JSON.parse(responseText)
            if (errorData.error === 'unsupported_server_type') {
              errorMessage = <>This server does not conform to the {MCP_SPEC_LINK}.</>
            } else if (errorData.message) {
              errorMessage = errorData.message
            }
          } catch {
            // If JSON parsing fails, use text content for fallback detection
            if (responseText.includes('well-known/oauth-authorization-server')) {
              errorMessage = <>This server does not conform to the {MCP_SPEC_LINK}.</>
            } else {
              errorMessage = responseText || errorMessage
            }
          }
        } catch {
          // If we can't read response at all, use fallback message
          errorMessage = 'Failed to add MCP server'
        }

        setUrlError(errorMessage)
        return
      }

      // Success: redirect to OAuth authorization flow
      const data = await response.json()
      const redirectUrl = data.redirect_url
      if (redirectUrl && typeof redirectUrl === 'string') {
        onDialogClose()
        window.location.assign(redirectUrl)
      } else {
        onDialogClose()
      }
    } catch (error: unknown) {
      // Handle unexpected errors (network, parsing, etc.)
      if (error instanceof Error) {
        setUrlError(error.message)
      } else {
        setUrlError('An unknown error occurred')
      }
    }
  }

  return (
    <>
      <AnchoredOverlay
        open={open}
        side="outside-top"
        align="start"
        onOpen={() => onPanelChange('add-mcp')}
        onClose={handleOverlayClose}
        renderAnchor={props => (
          <Button {...props} leadingVisual={ToolsIcon} variant="invisible" aria-label="MCPs">
            MCPs
          </Button>
        )}
      >
        <div className={styles.overlayContent}>
          {panel === 'add-mcp' && !showDialog && (
            <Button
              variant="invisible"
              leadingVisual={PlusIcon}
              ref={buttonRef}
              onClick={e => {
                e.stopPropagation()
                setShowDialog(true)
              }}
              className={styles.addMcpButton}
            >
              Add MCP
            </Button>
          )}
        </div>
      </AnchoredOverlay>
      {showDialog && (
        <Dialog
          title="Add remote MCP"
          onClose={onDialogClose}
          footerButtons={[
            {buttonType: 'default', content: 'Cancel', onClick: onDialogClose},
            {
              buttonType: 'primary',
              content: (
                <>
                  Connect to MCP
                  <span style={{marginLeft: 8}}>
                    <LinkExternalIcon size={16} />
                  </span>
                </>
              ),
              onClick: () => {
                void handleSubmit()
              },
            },
          ]}
          returnFocusRef={buttonRef}
        >
          <form className={styles.dialogForm} onSubmit={handleSubmit} autoComplete="on">
            <FormControl className={styles.formField}>
              <FormControl.Label>Name</FormControl.Label>
              <TextInput
                name="mcp-server-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter a name for this MCP"
                autoComplete="on"
                block
              />
            </FormControl>
            <FormControl required className={styles.formField}>
              <FormControl.Label>MCP server URL</FormControl.Label>
              <TextInput
                name="mcp-url"
                type="url"
                value={url}
                onChange={e => {
                  setUrl(e.target.value)
                  setUrlError(null)
                }}
                placeholder="https://mcp.example.com/sse"
                className={urlError ? styles.inputError : undefined}
                autoComplete="on"
                block
              />
              {urlError && <FormControl.Validation variant="error">{urlError}</FormControl.Validation>}
            </FormControl>
            {/* Hidden submit button required for Enter key submission */}
            <button type="submit" className={styles.hiddenSubmitButton} tabIndex={-1}>
              Submit
            </button>
            <p>
              MCP data will be processed by models coming from third-party providers. Always review the permissions
              required by the MCP and only connect to MCPs you know and trust. You can revoke access for the MCP at any
              time in{' '}
              <a href="/settings" className={styles.underline}>
                your settings
              </a>
              .
            </p>
          </form>
        </Dialog>
      )}
    </>
  )
}

try{ MCP_SPEC_LINK.displayName ||= 'MCP_SPEC_LINK' } catch {}
try{ ConfigureMcpServersMenu.displayName ||= 'ConfigureMcpServersMenu' } catch {}